import { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { SchemaSection } from '@rossum/api-client/schemas';
import { CopilotComposer } from '@rossum/components/copilot-dialog/CopilotComposer';
import { SuggesterIconButton } from '@rossum/components/variable-suggestions/SuggesterIconButton';
import { SuggestVariables } from '@rossum/components/variable-suggestions/SuggestVariables';
import { Stack, Tooltip, Typography } from '@rossum/ui/material';
import { DatasetColumn } from '../../../../datasets/types';
import { getFieldCompletions } from '../../../../formulas/getFieldCompletions';
import { baseSuggesterSections } from '../../../../formulas/suggesterSections';
import { handleInsert } from '../../../../rules/components/SuggestVariables/insertion-helpers';
import { PromptSuggestions } from './components/PromptSuggestions';
import { SelectDatasetChip } from './components/SelectDatasetChip';
import type { LookupResult } from './lookupResult';
import { useAgentSuggestLookup } from './useAgentSuggestLookup';

export const InlineLookupComposer = ({
  schemaContent,
  dataset,
  setDataset,
  datasetColumns,
  fieldSchemaId,
  label,
  onResult,
  queryIndex = null,
}: {
  schemaContent: SchemaSection[];
  dataset: string;
  setDataset?: (dataset: string) => void;
  datasetColumns?: (DatasetColumn & { samples: string[] })[];
  fieldSchemaId: string;
  label: string;
  onResult: (result: LookupResult) => void;
  /**
   * Which tier of the cascade this prompt is about. `null` is the whole thing — the first
   * suggestion, or a request to rework all of it.
   */
  queryIndex?: number | null;
}) => {
  const intl = useIntl();
  const { mutate, isLoading, isError, message } = useAgentSuggestLookup({
    fieldSchemaId,
    label,
    schemaContent,
    onResult,
  });

  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  const onSubmit = useCallback(
    (search: string) => {
      if (search) {
        mutate({ hint: search, queryIndex, dataset });
      }
    },
    [dataset, mutate, queryIndex]
  );

  const sections = useMemo(() => {
    const base = baseSuggesterSections(intl, {
      fields: getFieldCompletions(schemaContent),
      dataset:
        datasetColumns?.map(column => ({
          value: column.datasetAttributeName,
          meta: 'column',
          description: column.samples.join(', '),
          caption: column.name,
          score: 1,
        })) ?? [],
    });
    return [
      base.dataset,
      base.fields,
      base.annotation,
      base.document,
      base.email,
    ];
  }, [intl, schemaContent, datasetColumns]);

  return (
    <Stack spacing={1}>
      <Typography variant="body2" color="text.secondary">
        {intl.formatMessage({
          id: 'features.queueSettings.fields.lookup.inlineComposer.title',
        })}
      </Typography>
      <CopilotComposer
        value={value}
        setValue={setValue}
        inputRef={inputRef}
        placeholder={intl.formatMessage({
          id: 'features.queueSettings.fields.lookupCopilot.placeholder',
        })}
        onSubmit={onSubmit}
        isSuggesting={isLoading}
        isError={isError}
        // The agent's own words when it has them: it knows why it could not configure the field,
        // and the generic copy only tells the user to reword something it may have already read fine.
        errorText={
          message ??
          intl.formatMessage({
            id: 'features.queueSettings.fields.lookupCopilot.error',
          })
        }
        confirmText={
          isLoading
            ? intl.formatMessage({
                id: 'features.queueSettings.fields.lookupCopilot.confirm.inProgress',
              })
            : intl.formatMessage({
                id: 'features.queueSettings.fields.lookupCopilot.confirm',
              })
        }
        submitDisabled={isLoading || !dataset || value.length === 0}
        submitDisabledTooltipTitle={intl.formatMessage({
          id: 'features.queueSettings.fields.lookupCopilot.copilotSubmitDisabled',
        })}
        renderCopilotToolbar={
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <SelectDatasetChip
              selectedDataset={dataset}
              setSelectedDataset={setDataset}
              disabled={isLoading}
              dataCy="inline-lookup-select-dataset-chip"
            />
            <SuggestVariables
              sections={sections}
              mode="formula"
              onInsert={handleInsert({
                mode: 'textField',
                inputRef,
                currentValue: value,
                onChange: newValue => setValue(newValue),
              })}
            >
              <Tooltip
                title={intl.formatMessage({
                  id: 'features.queueSettings.fields.lookupCopilot.copilotVariable',
                })}
                placement="top"
              >
                <span>
                  <SuggesterIconButton
                    disabled={isLoading}
                    dataCy="inline-lookup-suggester-icon-button"
                  />
                </span>
              </Tooltip>
            </SuggestVariables>
          </Stack>
        }
      />
      <PromptSuggestions onPromptSelect={setValue} disabled={isLoading} />
    </Stack>
  );
};
